import * as Sentry from '@sentry/react';
import clientEnv from '../env.js';
import { escapeRegex } from './regex-utils.js';
import { shouldEnableSentry } from './analytics-consent.js';
let sentryInitialized = false;
/**
 * Initialize Sentry only when analytics consent allows it (banner or profile flags).
 * Safe to call repeatedly; initializes at most once per page load.
 */
export function tryInitSentryWithCurrentConsent(user) {
    if (typeof window === 'undefined')
        return;
    if (sentryInitialized)
        return;
    if (!shouldEnableSentry(user ?? null))
        return;
    try {
        if (!Sentry.init) {
            console.warn('Sentry is not available (may be blocked by ad blocker). Error tracking will be disabled.');
            return;
        }
        Sentry.init({
            dsn: 'https://e3c798408c63517c66ca6e2b44f84ba6@o4510272296779776.ingest.de.sentry.io/4510272319455312',
            environment: clientEnv.NODE_ENV,
            tracesSampleRate: 1.0,
            enableLogs: true,
            integrations: [
                Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
                Sentry.browserTracingIntegration(),
            ],
            release: clientEnv.REACT_APP_VERSION,
            tracePropagationTargets: (() => {
                const targets = ['localhost', /^https?:\/\/.*\/api\//];
                const apiUrls = [
                    clientEnv.REACT_APP_API_URL,
                    clientEnv.REACT_APP_API_SERVER,
                    clientEnv.REACT_APP_TUTOR_API_URL,
                    clientEnv.REACT_APP_MEETINGS_API_URL,
                ].filter(Boolean);
                apiUrls.forEach((url) => {
                    try {
                        const urlObj = new URL(url);
                        if (urlObj.hostname.length > 253) {
                            console.warn('Hostname too long, skipping:', urlObj.hostname);
                            return;
                        }
                        const escapedHostname = escapeRegex(urlObj.hostname);
                        targets.push(new RegExp(`^https?://${escapedHostname}`));
                    }
                    catch {
                        // skip invalid URL
                    }
                });
                return targets;
            })(),
            beforeSend(event, hint) {
                if (hint?.originalException) {
                    const error = hint.originalException;
                    if (error.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
                        error.message?.includes('sentry.io') ||
                        error.name === 'NetworkError') {
                        return null;
                    }
                }
                return event;
            },
        });
        sentryInitialized = true;
    }
    catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.debug('Sentry initialization failed (may be blocked by ad blocker):', error);
        }
    }
}
